(() => {
  'use strict';
  const T = window.theme || {};
  const root = (T.routes && T.routes.root) || '/';
  const withRoot = (path) => (root.endsWith('/') ? root : root + '/') + path.replace(/^\//, '');

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || T.moneyFormat || '€{{amount_with_comma_separator}}';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const withDelims = (n, precision, thousands, decimal) => {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(precision);
      const parts = n.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const c = parts[1] ? decimal + parts[1] : '';
      return dollars + c;
    };
    const m = fmt.match(re);
    if (!m) return fmt;
    let value = '';
    switch (m[1]) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(re, value);
  }

  /* ---------- Cart ---------- */
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let lastFocus = null;

  function openDrawer() {
    const d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    const panel = d.querySelector('.drawer__panel');
    if (panel) panel.focus();
  }
  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    if (lastFocus) lastFocus.focus();
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(withRoot('cart.js'), { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-cart-toggle]').forEach((el) => el.setAttribute('aria-label', `Cart, ${cart.item_count} items`));
      return cart;
    } catch (e) { return null; }
  }

  async function refreshDrawer() {
    const d = drawer();
    if (!d) return;
    try {
      const res = await fetch(withRoot('?section_id=cart-drawer'));
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer]');
      if (!fresh) return;
      const curPanel = d.querySelector('.drawer__panel');
      const newPanel = fresh.querySelector('.drawer__panel');
      if (curPanel && newPanel) curPanel.innerHTML = newPanel.innerHTML;
    } catch (e) { /* keep current drawer */ }
  }

  async function afterAdd() {
    await refreshCartCount();
    if (T.cartType === 'drawer' && drawer()) {
      await refreshDrawer();
      openDrawer();
    } else {
      window.location.href = T.routes.cart;
    }
  }

  async function addToCart(body) {
    const res = await fetch(T.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
      body: body instanceof FormData ? body : JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
    return data;
  }

  async function changeLine(line, quantity) {
    const container = document.querySelector('[data-cart-contents]') || document.querySelector('[data-cart-page]');
    if (container) container.classList.add('is-loading');
    try {
      const res = await fetch(T.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.description || data.message || 'Couldn’t update the cart. Refresh the page and try again.');
      }
    } finally {
      if (document.querySelector('[data-cart-page]')) { window.location.reload(); return; }
      await refreshCartCount();
      await refreshDrawer();
      if (container) container.classList.remove('is-loading');
    }
  }

  /* Product form submit */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-button]');
    const err = form.querySelector('[data-form-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
    const label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = T.strings.adding; }
    try {
      await addToCart(new FormData(form));
      await afterAdd();
    } catch (ex) {
      if (err) { err.textContent = ex.message || T.strings.error; err.hidden = false; }
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = label; }
    }
  });

  /* Quick add from product cards */
  document.addEventListener('click', async (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      quick.disabled = true;
      try {
        await addToCart({ items: [{ id: Number(quick.dataset.quickAdd), quantity: 1 }] });
        quick.classList.add('is-added');
        setTimeout(() => quick.classList.remove('is-added'), 1200);
        await afterAdd();
      } catch (ex) {
        alert(ex.message || T.strings.error);
      } finally {
        quick.disabled = false;
      }
      return;
    }

    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && T.cartType === 'drawer' && drawer() && !document.querySelector('[data-cart-page]')) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]')) { closeDrawer(); return; }

    const remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); changeLine(remove.dataset.lineRemove, 0); return; }

    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const wrap = qtyBtn.closest('[data-qty]');
      const input = wrap && wrap.querySelector('input');
      if (!input) return;
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value || 0) + Number(qtyBtn.dataset.qtyChange));
      input.value = next;
      if (input.dataset.lineQty) changeLine(input.dataset.lineQty, next);
      return;
    }

    const closeDetails = e.target.closest('[data-close-details]');
    if (closeDetails) {
      const d = closeDetails.closest('details');
      if (d) { d.open = false; document.body.classList.remove('no-scroll'); }
    }
  });

  document.addEventListener('change', (e) => {
    const lineInput = e.target.closest('[data-line-qty]');
    if (lineInput) { changeLine(lineInput.dataset.lineQty, Math.max(0, Number(lineInput.value || 0))); return; }
    if (e.target.matches('[data-autosubmit]')) {
      const form = e.target.form;
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const d = drawer();
    if (d && d.classList.contains('is-open')) { closeDrawer(); return; }
    document.querySelectorAll('details[open]').forEach((det) => {
      if (det.matches('[data-mobile-menu], [data-search], .header__dropdown, .filter')) {
        det.open = false;
        const s = det.querySelector('summary');
        if (s) s.focus();
      }
    });
    document.body.classList.remove('no-scroll');
  });

  /* Close open dropdowns when clicking elsewhere */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.header__dropdown[open], .filter[open], [data-search][open]').forEach((det) => {
      if (!det.contains(e.target)) det.open = false;
    });
  });

  /* Mobile menu locks scroll */
  document.addEventListener('toggle', (e) => {
    if (e.target.matches && e.target.matches('[data-mobile-menu]')) {
      document.body.classList.toggle('no-scroll', e.target.open);
    }
    if (e.target.matches && e.target.matches('[data-search]') && e.target.open) {
      const input = e.target.querySelector('input[type=search]');
      if (input) setTimeout(() => input.focus(), 30);
    }
  }, true);

  /* Desktop dropdowns open on hover */
  const hoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');
  document.querySelectorAll('[data-hover-details]').forEach((det) => {
    det.addEventListener('mouseenter', () => { if (hoverQuery.matches) det.open = true; });
    det.addEventListener('mouseleave', () => { if (hoverQuery.matches) det.open = false; });
  });

  /* Collection filters: drop empty fields so URLs stay clean */
  document.querySelectorAll('[data-filter-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      window.location.search = params.toString();
    });
  });

  /* Address delete confirm */
  document.querySelectorAll('form[data-confirm]').forEach((f) => {
    f.addEventListener('submit', (e) => { if (!confirm(f.dataset.confirm)) e.preventDefault(); });
  });

  /* ---------- Variant picker ---------- */
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const jsonEl = section.querySelector('[data-product-json]');
    if (!jsonEl) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    const picker = section.querySelector('[data-variant-picker]');
    if (!picker) return;
    const idInput = section.querySelector('[data-variant-id]');
    const addBtn = section.querySelector('[data-add-button]');
    const priceWrap = section.querySelector('[data-product-price] [data-price]');
    const gallery = section.querySelector('[data-gallery]');

    const selectedOptions = () => {
      const groups = picker.querySelectorAll('.variant-picker__group');
      return Array.from(groups).map((g) => {
        const checked = g.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    };

    const updateAvailability = (current) => {
      // Mark values that are sold out given the other selected options
      const groups = picker.querySelectorAll('.variant-picker__group');
      groups.forEach((g, idx) => {
        g.querySelectorAll('input').forEach((input) => {
          const test = current.slice();
          test[idx] = input.value;
          const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
          const label = g.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const update = () => {
      const opts = selectedOptions();
      opts.forEach((val, i) => {
        const lbl = picker.querySelector(`[data-option-label="${i}"]`);
        if (lbl) lbl.textContent = val || '';
      });
      updateAvailability(opts);
      const variant = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = T.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? T.strings.addToCart : T.strings.soldOut;
      }
      if (priceWrap) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.classList.toggle('price--sale', !!onSale);
        const hadSave = !!priceWrap.querySelector('.price__save');
        let html = onSale ? `<s class="price__compare"><span class="visually-hidden">Regular price</span>${formatMoney(variant.compare_at_price)}</s>` : '';
        html += `<span class="price__current">${onSale ? '<span class="visually-hidden">Sale price</span>' : ''}${formatMoney(variant.price)}</span>`;
        if (onSale && hadSave) html += `<span class="price__save">Save ${formatMoney(variant.compare_at_price - variant.price)}</span>`;
        priceWrap.innerHTML = html;
      }
      if (variant.featured_media && gallery) {
        const target = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (target) {
          if (gallery.scrollWidth > gallery.clientWidth) {
            gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
          } else if (target !== gallery.firstElementChild) {
            gallery.prepend(target);
          }
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    picker.addEventListener('change', update);
    updateAvailability(selectedOptions());
  });


  /* ---------- Predictive search ---------- */
  (function () {
    const input = document.querySelector('[data-predictive-input]');
    const results = document.querySelector('[data-predictive-results]');
    const clear = document.querySelector('[data-predictive-clear]');
    if (!input || !results) return;
    let t = null, ctrl = null;
    const run = async (q) => {
      if (ctrl) ctrl.abort();
      if (q.length < 2) { results.innerHTML = ''; return; }
      ctrl = new AbortController();
      try {
        const url = withRoot('search/suggest') + '?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last&section_id=predictive-search';
        const res = await fetch(url, { signal: ctrl.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sec = doc.querySelector('.shopify-section') || doc.body;
        results.innerHTML = sec.innerHTML;
      } catch (e) { /* aborted or offline */ }
    };
    input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => run(input.value.trim()), 250); });
    if (clear) clear.addEventListener('click', () => { results.innerHTML = ''; setTimeout(() => input.focus(), 0); });
  })();

  /* ---------- Product recommendations ---------- */
  document.querySelectorAll('[data-recommendations]').forEach(async (el) => {
    try {
      const res = await fetch(el.dataset.url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
    } catch (e) { /* section stays empty */ }
  });

  /* Theme editor: keep drawer usable when sections reload */
  document.addEventListener('shopify:section:load', () => refreshCartCount());
  /* Product card: bag icon opens sizes */
document.addEventListener('click', function (e) {
  document.querySelectorAll('[data-pc-sizes].is-open').forEach(function (p) {
    if (!p.parentNode.contains(e.target)) p.classList.remove('is-open');
  });
  var bag = e.target.closest('[data-pc-bag]');
  if (!bag) return;
  e.preventDefault();
  var panel = bag.parentNode.querySelector('[data-pc-sizes]');
  if (panel) panel.classList.toggle('is-open');
});
})();
